/* ==========================================================================
   Vidalite - Learn filter + zigzag re-stagger (progressive enhancement).

   Without this file the filter links are real anchors to
   {{ blog.url }}/tagged/<handle> and the server renders the correct set with
   a correct zigzag (Liquid stamps explicit --offset / --accent-* classes by
   forloop position over the rendered set). This script ENHANCES that: it
   filters the already-rendered cards in place (no reload), recomputes the
   stagger + accent over the VISIBLE subset, sets the active state, and
   announces the result count in a polite live region.

   THE ZIGZAG FIX: stagger (.learn__story--offset) and accent side
   (.learn__story--accent-left / --accent-right) are NEVER keyed to DOM order
   (no :nth-child, no cycle). They are explicit classes recomputed as a pure
   function of each card's VISIBLE index every time the filter or the
   breakpoint changes, so hidden cards never corrupt the pattern.

   ACCENT PEEK SAFETY: in two-column mode the accent frame must always peek
   toward the centre gutter, never toward a page edge or into the sidebar.
   Visible index parity maps directly to physical column (even = left column,
   odd = right column) because the CSS grid fills row-major with no offset
   reflow. So: left-column cards (even) get accent-right (peek right, toward
   the gutter); right-column cards (odd) get accent-left (peek left, toward
   the gutter). A lone trailing left-column card (odd total in 2-col) is
   forced to accent-left so its frame peeks toward the populated gutter rather
   than off the right edge. In single-column mode every card gets accent-right
   and no offset (matches the CSS single-column reset).

   Pagination honesty: if the blog spans more than one page (data-single-page
   is absent), JS does NOT intercept clicks - it lets the real /tagged anchor
   navigate so the server returns the complete tagged set. In-place filtering
   only runs when the whole blog fits on one page.

   Theme editor: init is idempotent and re-bound on shopify:section:load so a
   section re-render in the editor stays enhanced.
   ========================================================================== */
(function () {
  'use strict';

  // Matches the CSS single-column collapse at max-width:740px.
  var twoColMQ = window.matchMedia('(min-width: 741px)');

  function initRoot(root) {
    if (!root || root.dataset.learnFilterReady === '1') return;

    var grid = root.querySelector('[data-learn-grid]');
    if (!grid) return;
    root.dataset.learnFilterReady = '1';

    var links = Array.prototype.slice.call(root.querySelectorAll('.learn__filter-link'));
    var cards = Array.prototype.slice.call(grid.querySelectorAll('.learn__story'));
    var status = root.querySelector('[data-learn-status]');
    var empty = root.querySelector('[data-learn-empty]');
    var blogUrl = root.getAttribute('data-blog-url') || '';
    var singlePage = root.hasAttribute('data-single-page');

    /* Recompute stagger + accent over the VISIBLE cards only. The single
       source of truth post-enhancement. Accent side is derived from physical
       column parity so the tan frame always peeks toward the centre gutter. */
    function applyZigzag() {
      var visible = cards.filter(function (c) { return !c.hidden; });
      var twoCol = twoColMQ.matches;
      var last = visible.length - 1;
      visible.forEach(function (c, i) {
        var leftCol = (i % 2 === 0); // even visible index = left grid column
        // In 2-col, force the lone trailing left-column card to peek left
        // (toward the populated gutter) instead of off the right page edge.
        var lonelyLast = twoCol && leftCol && i === last && visible.length % 2 === 1;
        var accentRight = twoCol ? (leftCol && !lonelyLast) : true;
        c.classList.toggle('learn__story--accent-right', accentRight);
        c.classList.toggle('learn__story--accent-left', !accentRight);
        // Offset only the right-column card of each visible pair, 2-col only.
        c.classList.toggle('learn__story--offset', twoCol && !leftCol);
      });
      // Hygiene: clear stagger/accent drift on hidden cards so a future CSS
      // change that un-hides them never surfaces a stale offset/peek.
      cards.forEach(function (c) {
        if (c.hidden) {
          c.classList.remove('learn__story--offset');
          c.classList.remove('learn__story--accent-left');
          c.classList.add('learn__story--accent-right');
        }
      });
      return visible.length;
    }

    function setActive(handle) {
      links.forEach(function (l) {
        var on = l.getAttribute('data-filter') === handle;
        l.classList.toggle('is-active', on);
        if (on) l.setAttribute('aria-current', 'page');
        else l.removeAttribute('aria-current');
      });
    }

    function labelFor(handle) {
      if (handle === 'all') return '';
      var match = links.filter(function (l) { return l.getAttribute('data-filter') === handle; })[0];
      if (!match) return '';
      var name = match.querySelector('.learn__filter-name');
      return name ? name.textContent.trim() : '';
    }

    /* Single source of truth for the result message. Both the polite live
       region (SR) and the visible empty paragraph read from here so the copy
       can never drift between the two surfaces. */
    function messageFor(handle, n) {
      if (n === 0) return 'No articles in this topic yet.';
      var noun = (n === 1 ? ' article' : ' articles');
      if (handle === 'all') return 'Showing ' + n + noun + '.';
      var label = labelFor(handle);
      return 'Showing ' + n + noun + (label ? ' in ' + label + '.' : ' in this topic.');
    }

    function announce(handle, n) {
      if (status) status.textContent = messageFor(handle, n);
      if (empty) {
        empty.textContent = messageFor(handle, 0);
        empty.hidden = (n !== 0);
      }
    }

    function setFilter(handle, push) {
      cards.forEach(function (c) {
        var tags = (c.getAttribute('data-tags') || '').split(',').filter(Boolean);
        c.hidden = !(handle === 'all' || tags.indexOf(handle) !== -1);
      });
      var n = applyZigzag();
      setActive(handle);
      announce(handle, n);
      root.setAttribute('data-active-tag', handle);
      if (push) {
        var url = (handle === 'all')
          ? (blogUrl || location.pathname.replace(/\/tagged\/[^/?#]*.*$/, ''))
          : blogUrl + '/tagged/' + handle;
        try { history.pushState({ learnTag: handle }, '', url); } catch (e) {}
      }
    }

    // Derive a filter handle from a URL path (/tagged/<handle> -> handle).
    function handleFromPath(path) {
      var m = (path || '').match(/\/tagged\/([^/?#]+)/);
      return m ? decodeURIComponent(m[1]) : 'all';
    }

    links.forEach(function (l) {
      l.addEventListener('click', function (e) {
        // Let modified clicks (new tab, etc.) follow the real href.
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
        // Multi-page blogs: let the anchor navigate so the server returns the
        // complete tagged set with a correct server-rendered zigzag.
        if (!singlePage) return;
        e.preventDefault();
        setFilter(l.getAttribute('data-filter'), true);
        l.focus();
      });
    });

    // Browser back/forward through in-session filter states (single-page only).
    if (singlePage) {
      window.addEventListener('popstate', function () {
        setFilter(handleFromPath(location.pathname), false);
      });
    }

    // Re-stagger when crossing the 740/741 boundary (offsets on/off) without
    // re-running the filter: operate on the CURRENT visible set.
    var resizeTimer;
    var onChange = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(applyZigzag, 100);
    };
    if (twoColMQ.addEventListener) twoColMQ.addEventListener('change', onChange);
    else if (twoColMQ.addListener) twoColMQ.addListener(onChange);

    /* Initial pass: normalize classes over what the server rendered. On the
       index this is every card; on a /tagged direct hit the server already
       filtered, so the visible set equals the rendered set and this is an
       idempotent match of the Liquid stamping. Announce once so a landing
       user (incl. a deep-linked /tagged page) hears the count. */
    grid.classList.add('is-enhanced');
    var n = applyZigzag();
    var active = root.querySelector('.learn__filter-link.is-active');
    var handle = active ? active.getAttribute('data-filter') : 'all';
    setActive(handle);
    announce(handle, n);
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-learn-filter]');
    Array.prototype.forEach.call(roots, initRoot);
  }

  initAll();
  // Re-enhance sections injected/re-rendered by the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e && e.target ? e.target : document;
    var roots = scope.querySelectorAll
      ? scope.querySelectorAll('[data-learn-filter]')
      : [];
    if (roots.length) Array.prototype.forEach.call(roots, initRoot);
    else initAll();
  });
})();
